import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  useVillageWiseSakhiMandalCount,
  useVillageWiseSakhiMandalDetail,
  useSakhiMandalWiseMemberDetailDataPagination,
  useOverallSummaryCountNew
} from "../../../API_Master/FetchAPI";
import { useSakhiMandalMemberDetailExportData } from "../../../API_Master/ExcelAPI";
import { exportToExcel, notifyError } from "../../../MethodMaster/JSXComponentMothod";
import ExcelButton from "../../../CommonComponents/ExcelButton";
import PageEntries from "../../../CommonComponents/PageEntries";
import PaginationButtons from "../../../CommonComponents/PaginationButtons";
import TableShimmer from "../../../Components/TableShimmer";
import "./SakhiMandal.css";

const ShimmerCard = () => (
  <div className="govdash-villageCard-final">
    <div className="village-shimmer" style={{ height: "1rem", marginBottom: "0.6rem" }}></div>
    <div className="village-shimmer" style={{ height: "1.7rem" }}></div>
  </div>
);

const NoDataCard = () => (
  <div className="govdash-villageCard-final govdash-noVillageCard">
    <div className="govdash-noDataIcon">
      <i className="fas fa-village fa-2x"></i>
    </div>
    <div className="govdash-noDataTitle">No Data Found</div>
    <div className="govdash-noDataDesc">
      {document.querySelector(".govdash-searchInput")?.value ? "Try searching with different keywords" : "No data available"}
    </div>
  </div>
);

export default function SakhiMandal() {
  const { DarkTheme } = useSelector((state) => state.ProjectSlice);
  const [search, setSearch] = useState("");
  const [selectedVillage, setSelectedVillage] = useState(null);
  const [selectedMandali, setSelectedMandali] = useState(null);
  const themes = ["glass-0", "glass-1", "glass-2", "glass-3", "glass-4"];

  // Filter states for Level 3
  const [filterStatus, setFilterStatus] = useState(false);
  const filterRef = useRef(null);
  const [filterData, setFilterData] = useState({ shgCode: "", sakhiMandalName: "", memberCode: "", memberName: "" });
  const [filterInput, setFilterInput] = useState({ shgCode: "", sakhiMandalName: "", memberCode: "", memberName: "" });

  const { data: sakhiMandalData, isFetching: fetchingSakhiMandalData } = useVillageWiseSakhiMandalCount(!selectedVillage);
  const { data: overallSummaryData, isFetching: fetchingOverallSummaryData } = useOverallSummaryCountNew(!selectedVillage);

  const { data: sakhiMandalDetailData, isFetching: fetchingSakhiMandalDetailData } = useVillageWiseSakhiMandalDetail(
    !!selectedVillage,
    selectedVillage?.Village || selectedVillage?.VillageName
  );

  // Pagination states for Level 3
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const { data: memberData, fetchNextPage, hasNextPage, isFetching: isMemberFetching, isLoading: isMemberLoading, isError: isMemberError } = useSakhiMandalWiseMemberDetailDataPagination(
    !!selectedMandali,
    rowsPerPage,
    selectedMandali?.SakhiMandalName,
    selectedVillage?.Village || selectedVillage?.VillageName,
    filterData
  );

  const { mutate: exportSakhiMandalMembers, isPending: isExportingMembers } = useSakhiMandalMemberDetailExportData();

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedMandali]);

  const pages = memberData?.pages || [];
  const totalCount = memberData?.pages[0]?.TotalCount || 0;
  const currentItems = pages[currentPage]?.data || [];
  const start = currentPage * rowsPerPage + 1;
  const end = Math.min(start + currentItems.length - 1, totalCount);

  const handleRowsChange = async (e) => {
    const limit = parseInt(e.target?.value);
    setRowsPerPage(limit);
    setCurrentPage(0);
  };

  const goToPage = async (index) => {
    if (pages[index]) {
      setCurrentPage(index);
    } else if (hasNextPage) {
      await fetchNextPage();
      setCurrentPage(index);
    }
  };

  const handleExport = () => {
    toast.dismiss();
    if (!selectedVillage) {
      if (!filteredData.length) return notifyError("No Data Found");
      const exportData = filteredData.map((item, index) => ({
        "Sr No ": index + 1,
        "Village Name": item?.Village || "",
        "Total Sakhi Mandal": item?.TotalSakhiMandal || item?.TotalMandali || "",
        "Total Members": item?.TotalMember || item?.MemberCount || "",
      }));
      const sheetTitle = search ? `Sakhi Mandal - Search: ${search}` : "Sakhi Mandal";
      exportToExcel(exportData, "Sakhi_Mandal_Excel", {}, sheetTitle);
    } else if (!selectedMandali) {
      if (!sakhiMandalDetailData?.length) return notifyError("No Data Found");
      const exportData = sakhiMandalDetailData.map((item, index) => ({
        "Sr No ": index + 1,
        "Sakhi Mandal Name": item?.SakhiMandalName || "",
        "Member Count": item?.MemberCount || "",
      }));
      exportToExcel(exportData, "Sakhi_Mandal_Detail_Excel", {}, `Sakhi Mandal - ${selectedVillage?.Village || selectedVillage?.VillageName}`);
    } else {
      if (totalCount === 0) return notifyError("No Data Found");

      exportSakhiMandalMembers(
        {
          sakhiMandalName: selectedMandali?.SakhiMandalName,
          villageName: selectedVillage?.Village || selectedVillage?.VillageName,
          shgCode: filterData.shgCode || "",
          memberCode: filterData.memberCode || "",
          memberName: filterData.memberName || "",
          filterSakhiMandalName: filterData.sakhiMandalName || "",
        },
        {
          onSuccess: (NotificationData) => {
            if (!NotificationData || !NotificationData.length) {
              return notifyError("No Data Found");
            }
            const exportData = NotificationData.map((item, index) => ({
              "SR NO": index + 1,
              "MEMBER CODE": item?.MemberCode || "",
              "MEMBER NAME": item?.MemberName || "",
              "DOB": item?.DateOfBirth ? new Date(item.DateOfBirth).toLocaleDateString("en-GB") : "",
              "JOIN DATE": item?.DateOfJoiningInSHG ? new Date(item.DateOfJoiningInSHG).toLocaleDateString("en-GB") : "",
              "SHG CODE": item?.SHGCode || "",
              "SHG NAME": item?.SHGName || item?.SakhiMandalName || "",
              "FORMATION DATE": item?.DateOfFormation ? new Date(item.DateOfFormation).toLocaleDateString("en-GB") : "",
              "DESIGNATION": item?.DesignationInSHG || "",
              "MOBILE NO": item?.MobileNo || "",
              "VILLAGE": item?.Village || "",
              "GRAM PANCHAYAT": item?.GramPanchayat || "",
            }));

            exportToExcel(
              exportData,
              "Sakhi_Mandal_Members_Excel",
              {},
              `${selectedMandali?.SakhiMandalName} - ${selectedVillage?.Village || selectedVillage?.VillageName}`
            );
          },
          onError: () => {
            return notifyError("Export failed. Please try again.");
          },
        }
      );
    }
  };

  const handleBackToVillages = () => {
    setSelectedVillage(null);
    setSearch("");
  };

  const handleBackToMandalis = () => {
    setSelectedMandali(null);
    setSearch("");
    setFilterData({ shgCode: "", sakhiMandalName: "", memberCode: "", memberName: "" });
    setFilterInput({ shgCode: "", sakhiMandalName: "", memberCode: "", memberName: "" });
    setFilterStatus(false);
  };

  const handleFilterSearch = () => {
    setFilterData(filterInput);
    setCurrentPage(0);
    setFilterStatus(false);
  };

  const handleFilterCancel = () => {
    const empty = { shgCode: "", sakhiMandalName: "", memberCode: "", memberName: "" };
    setFilterInput(empty);
    setFilterData(empty);
    setCurrentPage(0);
    setFilterStatus(false);
  };

  // Compute filtered data at component level so handleExport can use it
  const filteredData = sakhiMandalData?.filter((v) => v.Village?.toLowerCase().includes(search.toLowerCase())) || [];

  // Level 3: Members Table View
  if (selectedVillage && selectedMandali) {
    return (
      <div className="flex-grow-1 w-100 mt-4">
        <button id="govdash-back-btn" className="govdash-backBtn mb-3" onClick={handleBackToMandalis}>
          Back
        </button>

        <div className={`govdash-villageDetailHeader mt-4`}>
          <div onClick={handleBackToMandalis} className={"govdash-detailIconWrapper cursor-pointer"}>
            <i className="govdash-VillageHeaderCard-icon fas fa-users"></i>
          </div>
          <div className={"govdash-detailInfo"}>
            <div className="d-flex align-items-center gap-2">
              <h2 className="govdash-detailName mb-0">{selectedMandali?.SakhiMandalName || "Sakhi Mandal"}</h2>
            </div>
            <div className={"govdash-detailStats"}>
              <div className={"govdash-statItem "}>
                <span className={"govdash-statLabel"}>Member Count</span>
                <span className={"govdash-statValue"}>{selectedMandali?.MemberCount || totalCount || "0"}</span>
              </div>
            </div>
          </div>
        </div>

        {isMemberLoading || isMemberError || isMemberFetching ? (
          <TableShimmer theme={DarkTheme ? "dark" : "light"} />
        ) : (
          <div className="govdash-tableContainer mt-4">
            <div className="govdash-tableHeader">
              <h2 className="govdash-sectionTitle">
                Member List
              </h2>
              <div className="govdash-tableActions">
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.85rem" }}>
                  <span style={{ whiteSpace: "nowrap" }}>Show</span>
                  <select
                    value={rowsPerPage}
                    onChange={handleRowsChange}
                    style={{
                      padding: "0.35rem 0.5rem",
                      borderRadius: "0.35rem",
                      border: "1px solid #d1d5db",
                      background: "#fff",
                      fontSize: "0.8rem",
                      minWidth: "60px",
                    }}
                  >
                    {[10, 25, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  <span style={{ whiteSpace: "nowrap" }}>entries</span>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                  <button onClick={() => setFilterStatus(true)} className="govdash-btn-outline">
                    <i className="fas fa-filter"></i> Filter
                    {(filterData.shgCode || filterData.sakhiMandalName || filterData.memberCode || filterData.memberName) && <span className="govdash-filterBadge">!</span>}
                  </button>
                  <ExcelButton isExportingMain={isExportingMembers} handleExportMain={handleExport} primaryExcel="Export to Excel" />
                </div>
              </div>
            </div>

            <div className="govdash-tableWrapper">
              <table className="govdash-table">
                <thead className="table-header-light border-top" id="tblHead">
                  <tr>
                    <th style={{ width: "3%" }}>SR NO</th>
                    <th style={{ width: "10%" }}>MEMBER CODE</th>
                    <th style={{ width: "15%" }}>MEMBER NAME</th>
                    <th style={{ width: "8%" }}>DOB</th>
                    <th style={{ width: "8%" }}>JOIN DATE</th>
                    <th style={{ width: "10%" }}>SHG CODE</th>
                    <th style={{ width: "15%" }}>SHG NAME</th>
                    <th style={{ width: "8%" }}>FORMATION DATE</th>
                    <th style={{ width: "8%" }}>DESIGNATION</th>
                    <th style={{ width: "10%" }}>MOBILE NO</th>
                    <th style={{ width: "10%" }}>VILLAGE</th>
                    <th style={{ width: "10%" }}>GRAM PANCHAYAT</th>
                  </tr>
                </thead>
                <tbody>
                  {pages[currentPage]?.data?.map((member, idx) => {
                    const absoluteIndex = currentPage * rowsPerPage + idx + 1;
                    const dob = member?.DateOfBirth ? new Date(member.DateOfBirth).toLocaleDateString("en-GB") : "-";
                    const joinDate = member?.DateOfJoiningInSHG ? new Date(member.DateOfJoiningInSHG).toLocaleDateString("en-GB") : "-";
                    const formationDate = member?.DateOfFormation ? new Date(member.DateOfFormation).toLocaleDateString("en-GB") : "-";

                    return (
                      <React.Fragment key={`member-${idx}`}>
                        <tr className="align-top">
                          <td>{absoluteIndex}</td>
                          <td>{member?.MemberCode || "-"}</td>
                          <td>{member?.MemberName || "-"}</td>
                          <td>{dob}</td>
                          <td>{joinDate}</td>
                          <td>{member?.SHGCode || "-"}</td>
                          <td>{member?.SHGName || member?.SakhiMandalName || "-"}</td>
                          <td>{formationDate}</td>
                          <td>{member?.DesignationInSHG || "-"}</td>
                          <td>{member?.MobileNo || "-"}</td>
                          <td>{member?.Village || "-"}</td>
                          <td>{member?.GramPanchayat || "-"}</td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>

              <div className="row mx-3 justify-content-between">
                <PageEntries start={start} end={end} totalCount={totalCount} />
                <PaginationButtons
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  goToPage={goToPage}
                  pages={pages}
                  hasNextPage={hasNextPage}
                  fetchNextPage={fetchNextPage}
                />
              </div>
            </div>
          </div>
        )}

        {/* Filter Sidebar */}
        <div className={`govdash-filterSidebar ${filterStatus ? "open" : ""}`} ref={filterRef}>
          <div className="govdash-filterHeader">
            <span>
              <i className="fas fa-filter"></i> Filter Members
            </span>
            <button onClick={() => setFilterStatus(false)} className="govdash-closeBtn">
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="govdash-filterBody">
            <div className="govdash-inputGroup">
              <label>SHG Code</label>
              <input
                type="text"
                placeholder="Enter SHG Code..."
                value={filterInput.shgCode}
                onChange={(e) => setFilterInput((p) => ({ ...p, shgCode: e.target.value }))}
              />
            </div>
            <div className="govdash-inputGroup">
              <label>Sakhi Mandal Name</label>
              <input
                type="text"
                placeholder="Enter Sakhi Mandal Name..."
                value={filterInput.sakhiMandalName}
                onChange={(e) => setFilterInput((p) => ({ ...p, sakhiMandalName: e.target.value }))}
              />
            </div>
            <div className="govdash-inputGroup">
              <label>Member Code</label>
              <input
                type="text"
                placeholder="Enter Member Code..."
                value={filterInput.memberCode}
                onChange={(e) => setFilterInput((p) => ({ ...p, memberCode: e.target.value }))}
              />
            </div>
            <div className="govdash-inputGroup">
              <label>Member Name</label>
              <input
                type="text"
                placeholder="Enter Member Name..."
                value={filterInput.memberName}
                onChange={(e) => setFilterInput((p) => ({ ...p, memberName: e.target.value }))}
              />
            </div>
          </div>
          <div className="govdash-filterFooter">
            <button onClick={handleFilterCancel} className="govdash-btn-secondary">
              Clear
            </button>
            <button onClick={handleFilterSearch} className="govdash-btn-primary">
              Apply Filters
            </button>
          </div>
        </div>

        {/* Overlay */}
        {filterStatus && <div className="govdash-sidebarOverlay" onClick={() => setFilterStatus(false)}></div>}
      </div>
    );
  }

  // Level 2: Mandali Grid View
  if (selectedVillage && !selectedMandali) {
    const filteredDetailData = sakhiMandalDetailData?.filter((v) => v.SakhiMandalName?.toLowerCase().includes(search.toLowerCase())) || [];

    return (
      <>
        <button id="govdash-back-btn" className="govdash-backBtn" onClick={handleBackToVillages}>
          Back
        </button>
        <div className={`govdash-villageDetailHeader mt-4`}>
          <div onClick={handleBackToVillages} className={"govdash-detailIconWrapper cursor-pointer"}>
            <i className="govdash-VillageHeaderCard-icon fas fa-house-user"></i>
          </div>
          <div className={"govdash-detailInfo"}>
            <div className="d-flex align-items-center gap-2">
              <h2 className="govdash-detailName mb-0">{selectedVillage?.Village || selectedVillage?.VillageName}</h2>
            </div>
            <div className={"govdash-detailStats"}>
              <div className={"govdash-statItem "}>
                <span className={"govdash-statLabel"}>Member Count</span>
                <span className={"govdash-statValue"}>{selectedVillage?.TotalMember || selectedVillage?.MemberCount || "0"}</span>
              </div>
              <div className={"govdash-statItem "}>
                <span className={"govdash-statLabel"}>Mandali Count</span>
                <span className={"govdash-statValue"}>{selectedVillage?.TotalSakhiMandal || selectedVillage?.TotalMandali || sakhiMandalDetailData?.length || "0"}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex flex-column flex-md-row justify-content-between gap-3 gap-md-0 mt-3 mb-3">
          <h3 className="govdash-sectionTitle mb-0">{selectedVillage?.Village || selectedVillage?.VillageName}</h3>
          <div className="d-flex gap-2 flex-grow-1 flex-md-grow-0">
            <div className="d-flex gap-3 align-items-center flex-wrap w-100">
              <div className="govdash-searchWrapper w-100">
                <i className="fas fa-search "></i>
                <input type="text" placeholder="Search Sakhi Mandal..." value={search} onChange={(e) => setSearch(e.target.value)} className="govdash-searchInput" />
                {search && (
                  <button onClick={() => setSearch("")} className="govdash-clearSearch">
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="govdash-villageGrid-final mt-4">
          {fetchingSakhiMandalDetailData ? (
            Array(8)
              .fill()
              .map((_, i) => <ShimmerCard key={i} />)
          ) : filteredDetailData.length > 0 ? (
            filteredDetailData.map((v, idx) => {
              const theme = themes[idx % 5];
              return (
                <div key={idx} className={`govdash-villageCard-final yojna-${theme}`} onClick={() => { setSelectedMandali(v); setSearch(""); }}>
                  <div className="village-header-final">
                    <div className="village-name-final">{v.SakhiMandalName || "Unknown"}</div>
                  </div>
                  <div className="beneficial-row-final">
                    <span className="label">Member Count</span>
                    <span className="value">{v.MemberCount || "0"}</span>
                  </div>
                </div>
              );
            })
          ) : (
            <NoDataCard />
          )}
        </div>
      </>
    );
  }

  // Level 1: Village Grid View

  const summaryCounts = {
    TotalVillages: overallSummaryData?.[0]?.TotalVillages || 0,
    TotalMandals: overallSummaryData?.[0]?.TotalMandals || 0,
    TotalMembers: overallSummaryData?.[0]?.TotalMembers || 0
  };

  return (
    <>
      {/* --- START OF NEW SUMMARY BANNER --- */}
      <div className="govdash-summaryGrid mt-3 mb-4">

        {/* Total Village Card */}
        <div className="govdash-summaryCard govdash-amountCard">
          <div className="govdash-cardContent">
            <i className="fas fa-house-user"></i>
            <div>
              <p className="govdash-cardLabel" style={{ fontSize: '18px' }}>Total Village</p>
              <h2 className="mb-0 fw-bold text-white">{summaryCounts.TotalVillages}</h2>
            </div>
          </div>
          <div className="govdash-cardBg"></div>
        </div>

        {/* Total Mandali Card */}
        <div className="govdash-summaryCard govdash-labhartiCard3">
          <div className="govdash-cardContent">
            <i className="fas fa-users"></i>
            <div>
              <p className="govdash-cardLabel" style={{ fontSize: '18px' }}>Total Sakhi Mandal</p>
              <h2 className="mb-0 fw-bold text-white">{summaryCounts.TotalMandals}</h2>
            </div>
          </div>
          <div className="govdash-cardBg"></div>
        </div>

        {/* Total Members Card */}
        <div className="govdash-summaryCard govdash-labhartiCard2">
          <div className="govdash-cardContent">
            <i className="fas fa-user-plus"></i>
            <div>
              <p className="govdash-cardLabel" style={{ fontSize: '18px' }}>Total Member</p>
              <h2 className="mb-0 fw-bold text-white">{summaryCounts.TotalMembers}</h2>
            </div>
          </div>
          <div className="govdash-cardBg"></div>
        </div>

      </div>
      {/* --- END OF NEW SUMMARY BANNER --- */}

      <div className="d-flex justify-content-between mt-3">
        <h3 className={"govdash-sectionTitle"}>Village Wise Sakhi Mandal Count</h3>
        <div className="d-flex gap-2">
          <div className="d-flex gap-3 align-items-center flex-wrap">
            <div className="govdash-searchWrapper">
              <i className="fas fa-search "></i>
              <input type="text" placeholder="Search Village Name..." value={search} onChange={(e) => setSearch(e.target.value)} className="govdash-searchInput" />
              {search && (
                <button onClick={() => setSearch("")} className="govdash-clearSearch">
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>
          <div>
            <button onClick={handleExport} className="govdash-excelBtn mt-4">
              <i className="fas fa-file-excel"></i>
              <span className="ms-2">Export Excel</span>
            </button>
          </div>
        </div>
      </div>

      <div className="govdash-villageGrid-final mt-4">
        {fetchingSakhiMandalData ? (
          Array(8)
            .fill()
            .map((_, i) => <ShimmerCard key={i} />)
        ) : filteredData.length > 0 ? (
          filteredData.map((v, idx) => {
            const theme = themes[idx % 5];
            const mandaliCount = v.TotalSakhiMandal || v.TotalMandali || v.MandaliCount || "0";
            const memberCount = v.TotalMember || v.MemberCount || v.TotalMembers || "0";
            const villageName = v.Village || v.VillageName || "Unknown";

            return (
              <div key={idx} className={`govdash-villageCard-final yojna-${theme}`} onClick={() => { setSelectedVillage(v); setSearch(""); }}>
                <div className="village-header-final">
                  <div className="village-name-final">{villageName}</div>
                </div>
                <div className="beneficial-row-final">
                  <span className="label">MANDALI COUNT</span>
                  <span className="value">{mandaliCount.toLocaleString()}</span>
                </div>

                <div className="member-card-minimal">
                  <div className="member-card-content">
                    <span className="member-card-label">Member Count</span>
                    <span className="member-card-value">{memberCount.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <NoDataCard />
        )}
      </div>
    </>
  );
}
